/**
 * Phaser Game Configuration
 *
 * Mobile-optimized Phaser configuration with Matter.js physics.
 * Includes high DPI support for retina displays.
 */

import Phaser from 'phaser';

// Get device pixel ratio for high DPI displays
const pixelRatio = window.devicePixelRatio || 1;

// Get device dimensions
const getGameSize = (): { width: number; height: number; zoom: number } => {
  const width = window.innerWidth;
  const height = window.innerHeight;

  // For high DPI displays, we'll use zoom instead of resolution multiplier
  // This keeps the logical size the same but renders at higher resolution
  // PERFORMANCE: Reduced from 3x to 2x cap - 3x was causing severe performance issues
  // on high-end devices (Pixel 8 Pro, etc.) by rendering millions of extra pixels
  const zoom = Math.min(pixelRatio, 2); // Cap at 2x for performance (was 3x)

  // Target portrait orientation primarily
  // Use device dimensions directly for mobile
  return {
    width: Math.min(width, 1080), // Cap at 1080p width for performance
    height: Math.min(height, 1920), // Cap at 1920p height
    zoom,
  };
};

const { width, height, zoom } = getGameSize();

console.log('🎮 Game Config:', {
  devicePixelRatio: pixelRatio,
  zoom,
  logicalSize: { width, height },
  physicalSize: { width: width * zoom, height: height * zoom },
});

export const gameConfig: Phaser.Types.Core.GameConfig = {
  // PHASE 2 TEST: Switch to Canvas renderer for better mobile performance
  // Research shows Canvas can be 30% faster on mobile devices
  // Previous: Phaser.WEBGL
  type: Phaser.CANVAS,
  width,
  height,
  parent: 'game-container',
  backgroundColor: '#000000',
  zoom, // Apply zoom for high DPI rendering

  // Physics engine - Matter.js for advanced 2D physics
  physics: {
    default: 'matter',
    matter: {
      gravity: {
        x: 0,
        y: 1.2, // Will be rotated based on tilt
      },
      debug: false, // Will be toggled in debug mode
      enableSleeping: false, // DISABLED: Breaks jump detection - sleeping platforms don't wake up properly

      // PHASE 4: Aggressively reduced physics iterations for mobile performance
      // Previous: 7+5+2 iterations (14 total)
      // New: 6+4+2 iterations (12 total - optimized for 60 FPS on mobile)
      // Combined with delta capping (33ms max), this maintains physics stability
      // while significantly reducing CPU overhead on mobile devices
      positionIterations: 6,
      velocityIterations: 4,

      // CRITICAL: Constrain engine timing to prevent huge delta spikes
      // This prevents physics from simulating too much time in one frame
      constraintIterations: 2,
    },
  },

  // Scale configuration for responsive design
  scale: {
    mode: Phaser.Scale.FIT, // Fit to screen while maintaining aspect ratio
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width,
    height,
    min: {
      width: 320,
      height: 568, // iPhone SE minimum
    },
    max: {
      width: 1080,
      height: 1920,
    },
  },

  // Render configuration optimized for high DPI
  render: {
    antialias: true, // Smooth edges on high DPI
    pixelArt: false, // We want smooth scaling, not pixelated
    roundPixels: false, // Allow sub-pixel rendering for crisp high DPI
    transparent: false,
    clearBeforeRender: true,
    preserveDrawingBuffer: false,
    failIfMajorPerformanceCaveat: false,
    powerPreference: 'high-performance', // Request high-performance GPU
  },

  // Performance settings
  fps: {
    target: 60,
    forceSetTimeOut: true, // CRITICAL: Force 60 FPS cap for consistent physics timing
    // Motion sensor games require stable frame times - uncapped RAF causes timing issues
    // on high refresh rate displays (120Hz+) and variable performance on low-end devices
    deltaHistory: 10,
    smoothStep: true, // Smooth out frame time variations
    // NOTE: Delta capping handled in GameScene.update() to prevent physics tunneling
  },

  // Audio configuration
  audio: {
    disableWebAudio: false,
    noAudio: false,
  },

  // Input configuration
  input: {
    activePointers: 3, // Support up to 3 touches (for testing)
    smoothFactor: 0.2, // Smooth pointer movement
  },

  // Disable right-click context menu
  disableContextMenu: true,

  // DOM element settings
  dom: {
    createContainer: true,
  },

  // Banner configuration (remove Phaser splash in production)
  banner: {
    hidePhaser: true,
    text: '#000000',
    background: ['#000000'],
  },
};
